import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import {
  XMarkIcon,
  GlobeAltIcon,
  BuildingOffice2Icon,
  MapPinIcon,
  UserIcon,
  CheckCircleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChatBubbleLeftRightIcon,
} from '@heroicons/react/24/outline';
import { trackEvent, getMetaBrowserIds } from '../lib/metaPixel';

const BUSINESS_WHATSAPP = '237653459638';

// TaraMoney payment endpoint (hosted on the CSL Sales / getkursa.space app).
const PAYMENT_API = (
  process.env.REACT_APP_PAYMENT_API_URL || 'https://www.getkursa.space'
).replace(/\/$/, '');

const EMAIL_RE = /\S+@\S+\.\S+/;

const STEP_KEYS = ['domain', 'company', 'location', 'contact', 'review'];
const STEP_ICONS = {
  domain: GlobeAltIcon,
  company: BuildingOffice2Icon,
  location: MapPinIcon,
  contact: UserIcon,
  review: CheckCircleIcon,
};

const EMPTY_FORM = {
  desiredDomain: '',
  hasDomain: false,
  companyName: '',
  companyType: '',
  employees: '',
  activity: '',
  country: '',
  city: '',
  fullName: '',
  whatsapp: '',
  email: '',
};

const inputClass =
  'w-full px-4 py-3 rounded-xl border border-gray-200 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-csl-green/30 focus:border-csl-green transition-all';
const labelClass =
  'block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1.5';

const ProEmailWizard = ({ pack, onClose }) => {
  const { t, i18n } = useTranslation();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(EMPTY_FORM);
  const [status, setStatus] = useState('idle'); // idle | submitting | success | error

  const companyTypes = useMemo(
    () => t('proEmail.wizard.companyTypes', { returnObjects: true }) || [],
    [t, i18n.language]
  );
  const employeeRanges = useMemo(
    () => t('proEmail.wizard.employeeRanges', { returnObjects: true }) || [],
    [t, i18n.language]
  );
  const countries = useMemo(
    () => t('proEmail.wizard.countries', { returnObjects: true }) || [],
    [t, i18n.language]
  );

  // Default the country to the first option (Cameroon/Cameroun) once available.
  useEffect(() => {
    setForm((prev) => {
      if (prev.country || !Array.isArray(countries) || !countries.length) return prev;
      return { ...prev, country: countries[0] };
    });
  }, [countries]);

  // Lock body scroll while the wizard is open.
  useEffect(() => {
    const original = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = original;
    };
  }, []);

  const stepKey = STEP_KEYS[step];
  const isLast = step === STEP_KEYS.length - 1;

  const update = (field) => (e) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const isStepValid = useMemo(() => {
    switch (stepKey) {
      case 'domain':
        return form.desiredDomain.trim().length > 2;
      case 'company':
        return (
          form.companyName.trim().length > 1 &&
          form.companyType &&
          form.employees
        );
      case 'location':
        return form.country && form.city.trim().length > 1;
      case 'contact':
        return (
          form.fullName.trim().length > 1 &&
          form.whatsapp.replace(/[^\d]/g, '').length >= 8 &&
          EMAIL_RE.test(form.email)
        );
      default:
        return true;
    }
  }, [stepKey, form]);

  const leadPayload = useMemo(
    () => ({
      pack: pack?.id,
      packLabel: pack?.name,
      packPrice: pack?.priceLabel,
      language: i18n.language,
      ...form,
    }),
    [pack, form, i18n.language]
  );

  const buildWhatsAppText = () => {
    const lines = [
      `🎯 NOUVEAU LEAD — Email Professionnel`,
      ``,
      `📦 Pack : ${pack?.name || '—'}${pack?.priceLabel ? ` (${pack.priceLabel})` : ''}`,
      ``,
      `🌐 Domaine souhaité : ${form.desiredDomain || '—'} (déjà possédé : ${form.hasDomain ? 'Oui' : 'Non'})`,
      `🏢 Entreprise : ${form.companyName || '—'} (${form.companyType || '—'})`,
      `👥 Effectif : ${form.employees || '—'}`,
      form.activity ? `💼 Secteur : ${form.activity}` : null,
      `📍 Localisation : ${[form.city, form.country].filter(Boolean).join(', ') || '—'}`,
      ``,
      `👤 Contact : ${form.fullName || '—'}`,
      `📱 WhatsApp : ${form.whatsapp || '—'}`,
      form.email ? `✉️ Email : ${form.email}` : null,
    ].filter(Boolean);
    return lines.join('\n');
  };

  const openWhatsAppFallback = () => {
    const url = `https://wa.me/${BUSINESS_WHATSAPP}?text=${encodeURIComponent(buildWhatsAppText())}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleNext = () => {
    if (!isStepValid) return;
    setStep((s) => Math.min(s + 1, STEP_KEYS.length - 1));
  };
  const handleBack = () => setStep((s) => Math.max(s - 1, 0));

  const trackingUserData = () => {
    const [firstName, ...rest] = (form.fullName || '').trim().split(/\s+/);
    return {
      email: form.email,
      phone: form.whatsapp,
      firstName,
      lastName: rest.join(' '),
      city: form.city,
      country: form.country,
    };
  };

  const handleSubmit = async () => {
    setStatus('submitting');

    // 1) Best-effort: push the lead to WhatsApp. This never blocks the payment
    //    flow — if it fails, the wa.me fallback below still captures the lead.
    try {
      await fetch('/api/send-lead', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(leadPayload),
      });
    } catch (_) {
      /* ignore — payment step and wa.me fallback remain available */
    }

    // 1b) Best-effort: register the lead as a hosting account on the OWS
    //     hosting manager (carries the WhatsApp number for follow-up).
    try {
      await fetch('/api/register-hosting', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(leadPayload),
      });
    } catch (_) {
      /* ignore — admin can still create the account manually */
    }

    trackEvent('Lead', {
      userData: trackingUserData(),
      customData: {
        content_name: `pro-email-${pack?.id}`,
        currency: 'USD',
        value: pack?.price,
      },
    });

    // 2) Generate the TaraMoney payment link and send the user to checkout.
    try {
      const locale = i18n.language && i18n.language.startsWith('en') ? 'en' : 'fr';

      trackEvent('InitiateCheckout', {
        userData: trackingUserData(),
        customData: {
          currency: 'USD',
          value: pack?.price,
          content_name: `pro-email-${pack?.id}`,
        },
      });

      const res = await fetch(`${PAYMENT_API}/api/tara/email-hosting`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tierId: pack?.id,
          name: form.fullName,
          email: form.email,
          whatsapp: form.whatsapp,
          locale,
          domain: form.desiredDomain,
          company: form.companyName,
          // Meta browser ids — forwarded so the backend's server-side
          // Purchase event can be matched to this browser session.
          ...getMetaBrowserIds(),
        }),
      });
      const json = await res.json().catch(() => null);
      if (!res.ok || !json?.redirectUrl) throw new Error('No payment link');
      setStatus('redirecting');
      window.location.href = json.redirectUrl;
    } catch (err) {
      // Payment link unavailable — fall back to WhatsApp so the lead is kept.
      setStatus('error');
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm p-0 sm:p-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          className="relative w-full sm:max-w-lg bg-white sm:rounded-3xl rounded-t-3xl shadow-2xl overflow-hidden max-h-[92vh] flex flex-col"
          initial={{ y: 40, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 40, opacity: 0 }}
          transition={{ type: 'spring', damping: 26, stiffness: 280 }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-csl-green to-csl-dark-green px-5 sm:px-7 py-5 text-white flex-shrink-0">
            <button
              onClick={onClose}
              aria-label="Close"
              className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center transition-colors"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
            <p className="text-white/80 text-xs font-semibold uppercase tracking-wider">
              {pack?.name} · {pack?.priceLabel}
            </p>
            <h3 className="text-lg sm:text-xl font-bold mt-0.5">{t('proEmail.wizard.title')}</h3>

            {status !== 'success' && status !== 'error' && status !== 'redirecting' && (
              <>
                {/* Step indicators */}
                <div className="flex items-center gap-1.5 mt-4">
                  {STEP_KEYS.map((k, idx) => (
                    <div
                      key={k}
                      className={`h-1.5 rounded-full flex-1 transition-colors ${idx <= step ? 'bg-white' : 'bg-white/30'
                        }`}
                    />
                  ))}
                </div>
                <p className="text-white/80 text-xs mt-2">
                  {t('proEmail.wizard.stepOf', { current: step + 1, total: STEP_KEYS.length })}
                </p>
              </>
            )}
          </div>

          {/* Body */}
          <div className="px-5 sm:px-7 py-6 overflow-y-auto flex-1">
            {status === 'redirecting' ? (
              <div className="flex flex-col items-center text-center py-10">
                <div className="w-12 h-12 rounded-full border-4 border-csl-green/20 border-t-csl-green animate-spin mb-5" />
                <p className="text-gray-700 text-sm font-medium max-w-xs">
                  {t('proEmail.wizard.redirecting')}
                </p>
              </div>
            ) : status === 'success' ? (
              <div className="flex flex-col items-center text-center py-8">
                <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-4">
                  <CheckCircleIcon className="w-9 h-9 text-csl-green" />
                </div>
                <h4 className="text-xl font-bold text-gray-900 mb-2">
                  {t('proEmail.wizard.success.title')}
                </h4>
                <p className="text-gray-600 text-sm max-w-sm">
                  {t('proEmail.wizard.success.message')}
                </p>
                <button
                  onClick={onClose}
                  className="mt-6 px-6 py-2.5 bg-csl-green text-white font-semibold rounded-xl hover:bg-csl-dark-green transition-colors text-sm"
                >
                  {t('proEmail.wizard.success.close')}
                </button>
              </div>
            ) : status === 'error' ? (
              <div className="flex flex-col items-center text-center py-8">
                <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-4">
                  <ChatBubbleLeftRightIcon className="w-9 h-9 text-csl-green" />
                </div>
                <p className="text-gray-600 text-sm max-w-sm mb-6">
                  {t('proEmail.wizard.error.message')}
                </p>
                <button
                  onClick={openWhatsAppFallback}
                  className="px-6 py-3 bg-[#25D366] text-white font-semibold rounded-xl hover:brightness-95 transition-all text-sm inline-flex items-center gap-2"
                >
                  <ChatBubbleLeftRightIcon className="w-5 h-5" />
                  {t('proEmail.wizard.error.whatsappFallback')}
                </button>
              </div>
            ) : (
              <AnimatePresence mode="wait">
                <motion.div
                  key={stepKey}
                  initial={{ opacity: 0, x: 24 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -24 }}
                  transition={{ duration: 0.2 }}
                  className="space-y-5"
                >
                  <div className="flex items-center gap-2 text-csl-green">
                    {React.createElement(STEP_ICONS[stepKey], { className: 'w-5 h-5' })}
                    <h4 className="text-base font-bold text-gray-900">
                      {t(`proEmail.wizard.${stepKey}.title`)}
                    </h4>
                  </div>

                  {/* Step: Domain */}
                  {stepKey === 'domain' && (
                    <>
                      <div>
                        <label className={labelClass}>
                          {t('proEmail.wizard.domain.desiredLabel')} <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={form.desiredDomain}
                          onChange={update('desiredDomain')}
                          placeholder={t('proEmail.wizard.domain.desiredPlaceholder')}
                          className={inputClass}
                          autoFocus
                        />
                        <p className="text-xs text-gray-400 mt-1.5">{t('proEmail.wizard.domain.hint')}</p>
                      </div>
                      <div>
                        <label className={labelClass}>{t('proEmail.wizard.domain.hasDomainLabel')}</label>
                        <div className="grid grid-cols-2 gap-3">
                          {[
                            { v: true, label: t('proEmail.wizard.domain.yes') },
                            { v: false, label: t('proEmail.wizard.domain.no') },
                          ].map((opt) => (
                            <button
                              key={String(opt.v)}
                              type="button"
                              onClick={() => setForm((p) => ({ ...p, hasDomain: opt.v }))}
                              className={`px-4 py-3 rounded-xl border text-sm font-medium transition-all ${form.hasDomain === opt.v
                                  ? 'border-csl-green bg-csl-green/10 text-csl-green'
                                  : 'border-gray-200 text-gray-600 hover:border-csl-green/40'
                                }`}
                            >
                              {opt.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    </>
                  )}

                  {/* Step: Company */}
                  {stepKey === 'company' && (
                    <>
                      <div>
                        <label className={labelClass}>
                          {t('proEmail.wizard.company.nameLabel')} <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={form.companyName}
                          onChange={update('companyName')}
                          placeholder={t('proEmail.wizard.company.namePlaceholder')}
                          className={inputClass}
                          autoFocus
                        />
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className={labelClass}>
                            {t('proEmail.wizard.company.typeLabel')} <span className="text-red-500">*</span>
                          </label>
                          <select
                            value={form.companyType}
                            onChange={update('companyType')}
                            className={`${inputClass} bg-white appearance-none`}
                          >
                            <option value="">{t('proEmail.wizard.company.typePlaceholder')}</option>
                            {companyTypes.map((c) => (
                              <option key={c} value={c}>{c}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className={labelClass}>
                            {t('proEmail.wizard.company.employeesLabel')} <span className="text-red-500">*</span>
                          </label>
                          <select
                            value={form.employees}
                            onChange={update('employees')}
                            className={`${inputClass} bg-white appearance-none`}
                          >
                            <option value="">{t('proEmail.wizard.company.employeesPlaceholder')}</option>
                            {employeeRanges.map((r) => (
                              <option key={r} value={r}>{r}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <div>
                        <label className={labelClass}>{t('proEmail.wizard.company.activityLabel')}</label>
                        <input
                          type="text"
                          value={form.activity}
                          onChange={update('activity')}
                          placeholder={t('proEmail.wizard.company.activityPlaceholder')}
                          className={inputClass}
                        />
                      </div>
                    </>
                  )}

                  {/* Step: Location */}
                  {stepKey === 'location' && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className={labelClass}>
                          {t('proEmail.wizard.location.countryLabel')} <span className="text-red-500">*</span>
                        </label>
                        <select
                          value={form.country}
                          onChange={update('country')}
                          className={`${inputClass} bg-white appearance-none`}
                        >
                          {countries.map((c) => (
                            <option key={c} value={c}>{c}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className={labelClass}>
                          {t('proEmail.wizard.location.cityLabel')} <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={form.city}
                          onChange={update('city')}
                          placeholder={t('proEmail.wizard.location.cityPlaceholder')}
                          className={inputClass}
                          autoFocus
                        />
                      </div>
                    </div>
                  )}

                  {/* Step: Contact */}
                  {stepKey === 'contact' && (
                    <>
                      <div>
                        <label className={labelClass}>
                          {t('proEmail.wizard.contact.fullNameLabel')} <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={form.fullName}
                          onChange={update('fullName')}
                          placeholder={t('proEmail.wizard.contact.fullNamePlaceholder')}
                          className={inputClass}
                          autoFocus
                        />
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className={labelClass}>
                            {t('proEmail.wizard.contact.whatsappLabel')} <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="tel"
                            value={form.whatsapp}
                            onChange={update('whatsapp')}
                            placeholder={t('proEmail.wizard.contact.whatsappPlaceholder')}
                            className={inputClass}
                          />
                        </div>
                        <div>
                          <label className={labelClass}>
                            {t('proEmail.wizard.contact.emailLabel')} <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="email"
                            value={form.email}
                            onChange={update('email')}
                            placeholder={t('proEmail.wizard.contact.emailPlaceholder')}
                            className={inputClass}
                          />
                        </div>
                      </div>
                      <p className="text-xs text-gray-400">{t('proEmail.wizard.contact.consent')}</p>
                    </>
                  )}

                  {/* Step: Review */}
                  {stepKey === 'review' && (
                    <div className="space-y-3">
                      <p className="text-sm text-gray-500">{t('proEmail.wizard.review.subtitle')}</p>
                      <dl className="rounded-2xl border border-gray-100 divide-y divide-gray-100 overflow-hidden">
                        {[
                          { label: t('proEmail.wizard.review.pack'), value: `${pack?.name} · ${pack?.priceLabel}` },
                          { label: t('proEmail.wizard.review.domain'), value: form.desiredDomain },
                          {
                            label: t('proEmail.wizard.review.company'),
                            value: `${form.companyName} (${form.companyType}) · ${form.employees}`,
                          },
                          {
                            label: t('proEmail.wizard.review.location'),
                            value: [form.city, form.country].filter(Boolean).join(', '),
                          },
                          {
                            label: t('proEmail.wizard.review.contact'),
                            value: `${form.fullName} · ${form.whatsapp}`,
                          },
                        ].map((row) => (
                          <div key={row.label} className="flex items-start justify-between gap-4 px-4 py-3 bg-gray-50/60">
                            <dt className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{row.label}</dt>
                            <dd className="text-sm text-gray-800 text-right break-words max-w-[60%]">{row.value || '—'}</dd>
                          </div>
                        ))}
                      </dl>
                    </div>
                  )}
                </motion.div>
              </AnimatePresence>
            )}
          </div>

          {/* Footer nav */}
          {status !== 'success' && status !== 'error' && status !== 'redirecting' && (
            <div className="flex items-center justify-between gap-3 px-5 sm:px-7 py-4 border-t border-gray-100 flex-shrink-0">
              <button
                type="button"
                onClick={handleBack}
                disabled={step === 0}
                className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-100 disabled:opacity-0 disabled:pointer-events-none transition-colors"
              >
                <ChevronLeftIcon className="w-4 h-4" />
                {t('proEmail.wizard.back')}
              </button>

              {isLast ? (
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={status === 'submitting'}
                  className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-semibold text-white bg-csl-green hover:bg-csl-dark-green disabled:opacity-70 transition-colors"
                >
                  {status === 'submitting'
                    ? t('proEmail.wizard.submitting')
                    : t('proEmail.wizard.submit')}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleNext}
                  disabled={!isStepValid}
                  className="inline-flex items-center gap-1.5 px-6 py-2.5 rounded-xl text-sm font-semibold text-white bg-csl-green hover:bg-csl-dark-green disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  {t('proEmail.wizard.next')}
                  <ChevronRightIcon className="w-4 h-4" />
                </button>
              )}
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ProEmailWizard;
